import React, { useState, useEffect } from 'react';
import './ProviderCodeGenerator.css';

interface ProviderCodeGeneratorProps {
  providerId: string;
  serviceUrl: string;
  providerName?: string;
}

const ProviderCodeGenerator: React.FC<ProviderCodeGeneratorProps> = ({
  providerId,
  serviceUrl,
  providerName = 'provider_name'
}) => {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  // Generate initial Docker Compose
  const generateInitialDockerCompose = (name: string, id: string, port: string) => `  ${name}:
    image: registry.gitlab.com/bb5102222/provider_native:prod
    ports:
      - ${port}:80
    env_file: stack.env
    environment:
      - ID_PROVIDER_IN_PLATFORM=${id}
      - LIST_IP_PROVIDER_WEBHOOK=fieldForFilter
      - BASE_ADDRESS_FOR_WEBHOOK=https://api.trustech.cash/provider/${id}/webhook
      - TIME_OUT_EXT_MS=3500
      - URL_FOR_HOST_2_HOST=https://api.trustech.cash
      - JWTTOKEN=<JWT_TOKEN>
    restart: unless-stopped
    deploy:
      resources:
        limits:
          memory: 512M
        reservations:
          memory: 512M
    logging:
      driver: json-file
      options:
        max-size: 2000m
        max-file: '5'`;

  // Generate initial Caddy Config
  const generateInitialCaddyConfig = (id: string, port: string) => `handle_path /provider/${id}/* {
    reverse_proxy 10.110.0.5:${port}
}`;

  // Extract port from serviceUrl
  const getPortFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.port || '5001';
    } catch {
      return '5001';
    }
  };

  const initialPort = getPortFromUrl(serviceUrl);
  const serviceName = providerName?.toLowerCase().replace(/\s+/g, '_') || 'provider_name';

  // Editable text areas
  const [dockerCompose, setDockerCompose] = useState(generateInitialDockerCompose(serviceName, providerId, initialPort));
  const [caddyConfig, setCaddyConfig] = useState(generateInitialCaddyConfig(providerId, initialPort));
  const [webhookUrl, setWebhookUrl] = useState(`https://api.trustech.cash/provider/${providerId}/webhook`);

  // Update when props change
  useEffect(() => {
    const port = getPortFromUrl(serviceUrl);
    const name = providerName?.toLowerCase().replace(/\s+/g, '_') || 'provider_name';
    setDockerCompose(generateInitialDockerCompose(name, providerId, port));
    setCaddyConfig(generateInitialCaddyConfig(providerId, port));
    setWebhookUrl(`https://api.trustech.cash/provider/${providerId}/webhook`);
  }, [providerId, providerName, serviceUrl]);

  // Copy to clipboard
  const copyToClipboard = async (text: string, section: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Ошибка при копировании:', err);
    }
  };

  // Copy all
  const copyAll = async () => {
    const fullCode = `# === Docker Compose ===\n${dockerCompose}\n\n# === Caddy Config ===\n${caddyConfig}\n\n# === Webhook URL ===\n${webhookUrl}`;
    await copyToClipboard(fullCode, 'all');
  };

  return (
    <div className="provider-code-generator">
      <div className="code-section">
        <div className="code-section-header">
          <span className="code-section-title">Docker Compose (stack.yml)</span>
          <button onClick={() => copyToClipboard(dockerCompose, 'docker')}>
            {copiedSection === 'docker' ? '✓ Скопировано' : 'Копировать'}
          </button>
        </div>
        <textarea
          className="code-textarea"
          value={dockerCompose}
          onChange={e => setDockerCompose(e.target.value)}
          rows={20}
        />
      </div>

      <div className="code-section">
        <div className="code-section-header">
          <span className="code-section-title">Caddy Config</span>
          <button onClick={() => copyToClipboard(caddyConfig, 'caddy')}>
            {copiedSection === 'caddy' ? '✓ Скопировано' : 'Копировать'}
          </button>
        </div>
        <textarea
          className="code-textarea"
          value={caddyConfig}
          onChange={e => setCaddyConfig(e.target.value)}
          rows={3}
        />
      </div>

      <div className="code-section">
        <div className="code-section-header">
          <span className="code-section-title">Webhook URL</span>
          <button onClick={() => copyToClipboard(webhookUrl, 'webhook')}>
            {copiedSection === 'webhook' ? '✓ Скопировано' : 'Копировать'}
          </button>
        </div>
        <textarea
          className="code-textarea"
          value={webhookUrl}
          onChange={e => setWebhookUrl(e.target.value)}
          rows={1}
        />
      </div>

      <button className="copy-all-btn" onClick={copyAll}>
        {copiedSection === 'all' ? '✓ Всё скопировано!' : 'Копировать всё'}
      </button>
    </div>
  );
};

export default ProviderCodeGenerator;
